import { ChangeEvent, CSSProperties, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Line } from "react-chartjs-2"
import { createPortal } from "react-dom"
import classNames from "classnames"

import { Spinner } from "components/common/Spinner"

import { chartColors, type ChartContext, compactLineOptions, createGradient } from "./chartConfig"
import type { MiniTrendColumnData } from "./dashboardData"

import styles from "./index.module.scss"

export type SelectOption = string | { label: string; value: string }

const getSelectOptionLabel = (option: SelectOption) => (typeof option === "string" ? option : option.label)
const getSelectOptionValue = (option: SelectOption) => (typeof option === "string" ? option : option.value)

export type SelectPillProps = {
  options: SelectOption[]
  label?: string
  value?: string
  disabled?: boolean
  onChange?: (option: string) => void
}

export const SelectPill = ({ options, label, value, disabled, onChange }: SelectPillProps) => (
  <label className={styles.selectPill}>
    {label && <span className={styles.selectLabel}>{label}</span>}
    <select
      value={value ?? (options[0] ? getSelectOptionValue(options[0]) : "")}
      aria-label={label ?? (options[0] ? getSelectOptionLabel(options[0]) : "Select option")}
      disabled={disabled || options.length === 0}
      onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange?.(event.target.value)}
    >
      {options.map(option => (
        <option key={getSelectOptionValue(option)} value={getSelectOptionValue(option)}>
          {getSelectOptionLabel(option)}
        </option>
      ))}
    </select>
  </label>
)

type SectionProps = {
  title: string
  children: ReactNode
  controls?: ReactNode
  className?: string
}

export const Section = ({ title, children, controls, className }: SectionProps) => (
  <section className={classNames(styles.section, className)}>
    <div className={styles.sectionHeader}>
      <h2>{title}</h2>
      {controls && <div className={styles.controls}>{controls}</div>}
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
)

type ChartCardProps = {
  title: string
  children: ReactNode
  className?: string
}

export const ChartCard = ({ title, children, className }: ChartCardProps) => (
  <article className={classNames(styles.chartCard, className)}>
    <h3>{title}</h3>
    {children}
  </article>
)

export type LegendItem = {
  label: string
  value?: number
  color: string
}

export const Legend = ({ items }: { items: LegendItem[] }) => (
  <div className={styles.legend}>
    {items.map(item => (
      <span key={item.label} className={styles.legendItem}>
        <i style={{ backgroundColor: item.color }} />
        <span>{item.label}</span>
        {item.value !== undefined && <strong>{item.value}</strong>}
      </span>
    ))}
  </div>
)

const TOOLTIP_MAX_WIDTH = 245
const TOOLTIP_VIEWPORT_MARGIN = 12
const TOOLTIP_ICON_GAP = 16

const InfoTooltip = ({ text }: { text: string }) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isRendered, setRendered] = useState(false)
  const [isVisible, setVisible] = useState(false)
  const [position, setPosition] = useState({ arrowOffset: 0, left: 0, top: 0 })

  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()

    if (!rect) return

    const iconCenter = rect.left + rect.width / 2
    const minLeft = TOOLTIP_VIEWPORT_MARGIN + TOOLTIP_MAX_WIDTH / 2
    const maxLeft = window.innerWidth - TOOLTIP_VIEWPORT_MARGIN - TOOLTIP_MAX_WIDTH / 2
    const left = Math.min(Math.max(iconCenter, minLeft), maxLeft)

    setPosition({
      arrowOffset: iconCenter - left,
      left,
      top: rect.top - TOOLTIP_ICON_GAP,
    })
  }, [])

  const showTooltip = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }

    updatePosition()
    setRendered(true)
    requestAnimationFrame(() => setVisible(true))
  }

  const hideTooltip = () => {
    setVisible(false)
    hideTimerRef.current = setTimeout(() => {
      setRendered(false)
    }, 180)
  }

  useLayoutEffect(() => {
    if (!isRendered) return undefined

    updatePosition()
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)

    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [isRendered, updatePosition])

  useEffect(
    () => () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
      }
    },
    [],
  )

  const tooltipStyle: CSSProperties & { "--tooltip-arrow-offset": string } = {
    "--tooltip-arrow-offset": `${position.arrowOffset}px`,
    left: position.left,
    top: position.top,
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={text}
        className={styles.infoButton}
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        i
      </button>
      {isRendered &&
        createPortal(
          <span
            className={classNames(styles.tooltipBubble, { [styles.tooltipBubbleVisible]: isVisible })}
            role="tooltip"
            style={tooltipStyle}
          >
            {text}
          </span>,
          document.body,
        )}
    </>
  )
}

type MetricCardProps = {
  title: string
  value: string
  helper: string
  segments?: { label: string; value: string }[]
  isLoading?: boolean
}

export const MetricCard = ({ title, value, helper, segments, isLoading }: MetricCardProps) => (
  <article className={styles.metricCard}>
    <div className={styles.metricHeader}>
      <span>{title}</span>
      <span className={styles.tooltipWrapper}>
        <InfoTooltip text={helper} />
      </span>
    </div>
    <div className={styles.metricContent}>
      <strong>{isLoading ? <Spinner width={24} height={24} /> : value}</strong>
      {segments && (
        <div className={styles.metricSegments}>
          {segments.map(segment => (
            <span key={segment.label}>
              <b>{segment.value}</b>
              {segment.label}
            </span>
          ))}
        </div>
      )}
    </div>
  </article>
)

export const MiniTrendCard = ({
  title,
  controls,
  columns,
}: {
  title: string
  controls: ReactNode
  columns: MiniTrendColumnData[]
}) => (
  <article className={styles.trendCard}>
    <div className={styles.trendHeader}>
      <h3>{title}</h3>
      {controls}
    </div>
    <div className={styles.trendColumns}>
      {columns.map(column => (
        <div key={column.label} className={styles.trendColumn}>
          <div className={styles.miniChart}>
            <Line
              data={{
                labels: column.chartData.map((_, index) => String(index + 1)),
                datasets: [
                  {
                    data: column.chartData,
                    borderColor: chartColors.active,
                    backgroundColor: (context: ChartContext) => createGradient(context, "rgba(34, 197, 94, 0.24)"),
                    fill: true,
                    pointRadius: 0,
                    borderWidth: 2,
                    tension: 0.35,
                  },
                ],
              }}
              options={compactLineOptions}
            />
          </div>
          <span className={styles.trendLabel}>{column.label}</span>
          <div className={styles.trendValue}>
            {column.value}
            {column.subValue && <small>{column.subValue}</small>}
          </div>
          <div className={classNames(styles.delta, styles[column.deltaType])}>
            <span>{column.delta}</span>
            <small>{column.trendDays ?? "past 7 days"}</small>
          </div>
        </div>
      ))}
    </div>
  </article>
)
